import {CommonModule} from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  EventEmitter,
  input,
  Output,
  ViewChild,
} from '@angular/core';
import {FormControl, FormGroup, FormsModule, Validators} from '@angular/forms';
import {Store} from '@ngrx/store';
import {Bundle} from '@sportngin/se-commerce-sdk/models/clare';
import {distinctUntilChanged, map, Observable} from 'rxjs';
import {SeFeAlertModule} from 'se-fe-alert';
import {SeFeButtonModule} from 'se-fe-button';
import {SeFeFormFieldConfig, SeFeFormFieldModule} from 'se-fe-form-field';
import {SeFeFormFieldTextModule} from 'se-fe-form-field-text';
import {SeFeFormServiceModule} from 'se-fe-form-service';
import {SeFeGridModule} from 'se-fe-grid';
import {SeFeHeaderModule} from 'se-fe-header';
import {SeFeIconModule} from 'se-fe-icon';
import {SeFeLinkModule} from 'se-fe-link';
import {SeFeModalComponent, SeFeModalFooterComponent, SeFeModalModule} from 'se-fe-modal';
import {SeFePaginationOptions} from 'se-fe-pagination';
import {ProductQueryParams} from '../../../../../models';
import {clareProductToProduct, Product} from '../../../../../models/clare';
import {buildPaginationOptions} from '../../../../../utils/pagination-utils';
import {
  ProductSelectionChange,
  ProductSelectionTableComponent,
} from '../../../../../common/components/product-selection-table/product-selection-table.component';
import {ProductListActions} from '../../../../product-list/store/product-list.actions';
import {
  selectPaginationMetaForSelection,
  selectProductListLoadingForSelection,
  selectProductQuery,
  selectProductsForSelection,
} from '../../../../product-list/store/product-list.selectors';
import {BundleSelectionState} from './bundle-selection-state';

export interface ProductsSelectedEvent {
  products: Product[];
  price?: number;
}

@Component({
  selector: 'choose-bundle-products-modal',
  imports: [
    CommonModule,
    FormsModule,
    ProductSelectionTableComponent,
    SeFeAlertModule,
    SeFeButtonModule,
    SeFeFormFieldModule,
    SeFeFormFieldTextModule,
    SeFeFormServiceModule,
    SeFeGridModule,
    SeFeHeaderModule,
    SeFeIconModule,
    SeFeLinkModule,
    SeFeModalModule,
  ],
  templateUrl: './choose-bundle-products-modal.component.html',
  styleUrls: ['./choose-bundle-products-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {class: 'choose-bundle-products-modal'},
})
/**
 * Modal for selecting products to include in a bundle.
 */
export class ChooseBundleProductsModalComponent {
  @ViewChild('chooseBundleProducts') chooseBundleProducts: SeFeModalComponent;
  @ViewChild('chooseBundleProductsFooter') chooseBundleProductsFooter: SeFeModalFooterComponent;

  /**
   * Emitted when products are selected and saved.
   * Emits the full array of selected products.
   */
  @Output() productsSelected = new EventEmitter<ProductsSelectedEvent>();

  bundle = input<Bundle>();

  /**
   * Products already included in the bundle.
   * Used to maintain consistency when reopening the modal.
   */
  existingProducts = input<Product[]>([]);

  /**
   * Mode determines behavior:
   * - 'create': Simple selection only (no pricing)
   * - 'edit': Includes pricing logic for bundle details page
   */
  mode = input<'create' | 'edit'>('create');

  loading$!: Observable<boolean>;
  products$!: Observable<Product[]>;
  paginationOptions$!: Observable<SeFePaginationOptions>;
  queryString$!: Observable<string | undefined>;

  /** Selection state; exposed for tests. */
  selectionState = new BundleSelectionState();

  bundlePriceConfig: SeFeFormFieldConfig = {
    label: 'Bundle price',
    errorMessages: {
      required: 'Bundle price is required',
      min: 'Price must be greater than 0',
      pattern: 'Price must be a valid number',
    },
  };
  bundlePriceForm = new FormGroup({
    price: new FormControl('', [Validators.required, Validators.min(0), Validators.pattern('^[0-9]+(.[0-9]{1,2})?$')]),
  });

  get selectedProducts(): Product[] {
    return this.selectionState.getSelectedProducts();
  }

  get queryString(): string | undefined {
    return this.initialQueryResetDone ? this.currentSearchQuery : '';
  }

  private modalCurrentPage = 1;
  private modalPageSize = 25;
  private isModalOpen = false;
  private initialQueryResetDone = false;
  private currentSearchQuery = '';

  constructor(
    private store: Store,
    private cdr: ChangeDetectorRef
  ) {
    this.products$ = this.store
      .select(selectProductsForSelection)
      .pipe(map(items => (items ?? []).map(clareProductToProduct)));
    this.loading$ = this.store.select(selectProductListLoadingForSelection).pipe(distinctUntilChanged());

    this.paginationOptions$ = this.store.select(selectPaginationMetaForSelection).pipe(
      map(meta => {
        const options = buildPaginationOptions(meta, {label: 'Items', defaultLimit: 25});
        this.modalCurrentPage = options.currentPage ?? 1;
        this.modalPageSize = options.limit ?? 25;

        return options;
      })
    );

    this.queryString$ = this.store.select(selectProductQuery);
    effect(() => {
      if (this.bundle()) {
        const price = String(this.bundle()!.price_cents / 100);
        this.bundlePriceForm.patchValue({price});
      }
    });
  }

  get selectedProductIds(): Set<string> {
    return this.selectionState.getSelectedProductIdsSnapshot();
  }

  /**
   * Handles the selection changes of the products.
   * @param event - Current page context and selected products from the table.
   */
  onSelectionChange(event: ProductSelectionChange): void {
    this.selectionState.mergeSelectionFromPage(event.selectedProducts, event.currentPageProductIds);
    this.cdr.markForCheck();
  }

  /**
   * Handles the Save action.
   * Emits the selected products (which may be empty to remove all products from a bundle).
   */
  handleAction(): void {
    const products = this.selectionState.getSelectedProducts();

    if (this.mode() === 'edit' && products.length > 0 && !this.bundlePriceForm.valid) {
      this.bundlePriceForm.markAllAsTouched();
      this.bundlePriceForm.markAllAsDirty();
      this.bundlePriceForm.updateValueAndValidity();

      return;
    }

    const productsToEmit: ProductsSelectedEvent = {products};
    if (this.mode() === 'edit') {
      productsToEmit.price = products.length > 0 ? Number(this.bundlePriceForm.get('price')?.value) : 0;
    }
    this.productsSelected.emit(productsToEmit);
    this.chooseBundleProducts.close();
  }

  /**
   * Handles product list query changes (page, sort, search).
   * Dispatches loadProductsForSelection (products only, no bundles).
   */
  queryChanged(queryParams: ProductQueryParams): void {
    const finalParams = {
      page: queryParams?.page ?? 1,
      take: queryParams?.take ?? 25,
      query: queryParams?.query ?? '',
      sortBy: queryParams?.sortBy,
      direction: queryParams?.direction,
      preventNavigation: true,
    };
    this.currentSearchQuery = finalParams.query;
    this.initialQueryResetDone = true;

    this.store.dispatch(
      ProductListActions.loadProductsForSelection({
        queryParams: finalParams,
      })
    );
  }

  /**
   * Opens the modal and initializes with existing products if any.
   * @param event - The event that triggered the modal.
   * @param triggerEl - The trigger element that opened the modal.
   */
  onOpenModal(eventOrTrigger: Event | HTMLElement): void {
    const event = this.asDomEvent(eventOrTrigger);
    event?.preventDefault();
    event?.stopPropagation();

    const triggerEl = this.resolveTriggerElement(eventOrTrigger);
    if (!triggerEl) {
      return;
    }
    this.openModalAndInitSelections(triggerEl);
    this.loadProductsForModal();
    this.cdr.markForCheck();
  }

  /**
   * Resets the bundle price to the current value from the bundle, if available.
   */
  resetBundlePrice(): void {
    const price = this.bundle() ? String(this.bundle()!.price_cents / 100) : '';
    this.bundlePriceForm.patchValue({price});
  }

  /** Opens the modal UI and initializes selection state from existing products. */
  private openModalAndInitSelections(triggerEl: HTMLElement): void {
    this.chooseBundleProducts.open(triggerEl);
    this.isModalOpen = true;

    const existing = this.existingProducts();
    this.selectionState.reset(existing);
    this.cdr.markForCheck();
  }

  /** Dispatches loadProductsForSelection (products only) for the modal's initial page/size. */
  private loadProductsForModal(): void {
    this.store.dispatch(
      ProductListActions.loadProductsForSelection({
        queryParams: {
          page: this.modalCurrentPage,
          take: this.modalPageSize,
          query: '',
          preventNavigation: true,
        },
      })
    );
  }

  /**
   * Resets the selected products and bundle prices when the modal is closed.
   * Note: Pre-selection of existing products happens in onOpenModal() to maintain
   * consistency between saved list and modal selections.
   */
  modalClosed(): void {
    this.isModalOpen = false;
    this.selectionState.reset();
    this.currentSearchQuery = '';
    this.initialQueryResetDone = false;
    this.resetBundlePrice();
    this.cdr.markForCheck();
  }

  private asDomEvent(eventOrTrigger: Event | HTMLElement): Event | null {
    return typeof (eventOrTrigger as Event).preventDefault === 'function' ? (eventOrTrigger as Event) : null;
  }

  private resolveTriggerElement(eventOrTrigger: Event | HTMLElement): HTMLElement | null {
    if (eventOrTrigger instanceof HTMLElement) {
      return eventOrTrigger;
    }

    const currentTarget = eventOrTrigger.currentTarget;
    if (currentTarget instanceof HTMLElement) {
      return currentTarget;
    }

    const target = eventOrTrigger.target;
    return target instanceof HTMLElement ? target : null;
  }
}
